import { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/constants";
import { STATUS_COLORS, fmtCash, CCY_SYMBOL, fmtCcy, pluralLabel } from "./dashboard/formatters";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Target,
  Clock,
  Trophy,
  Users,
  FileText,
  ChevronUp,
  ChevronDown,
  Pencil,
  Check,
  X,
  DollarSign,
  AlertTriangle,
  Calendar,
  ExternalLink,
  Info,
  RefreshCw,
  Loader2,
  Zap,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  Globe,
  Wifi,
  WifiOff,
  Activity,
  HelpCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import RevenuePage from "./RevenuePage";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { toast } from "sonner";

const STAGE_COLORS = {
  "Unqualified Leads": "#94a3b8",
  "First Contact Made": "#60a5fa",
  "Discovery Conversation Held": "#3b82f6",
  "RFP Received": "#6366f1",
  "Diagnostic / Briefing Phase": "#8b5cf6",
  "Non-Financial Proposal Sent": "#d946ef",
  "Proposal Submitted": "#f59e0b",
  "Commercial Negotiation": "#f97316",
  "Verbal Agreement": "#84cc16",
  "Contract Signed": "#10b981",
  "Onboarding": "#14b8a6",
  "Delivery in Progress": "#06b6d4",
  "Delivery Completed": "#0ea5e9",
  "Expansion / Upsell Discussion": "#a855f7",
  "Strategic Entry Proposed": "#2dd4bf",
  "Strategic Entry Delivered": "#0d9488",
  "Expansion Conversation": "#c084fc",
  "Closed \u2013 Won": "#16a34a",
  "Closed \u2013 Not Proceeding": "#ef4444",
};

const TREND_COLORS = {
  meetings: "#2563eb",
  proposals: "#8b5cf6",
  wins: "#10b981",
};

function MetricCard({ title, value, subtitle, icon: Icon, change, color = "blue", onClick }) {
  const isPositive = change > 0;
  const isNeutral = change === 0;
  const colorMap = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-violet-50 text-violet-600",
    emerald: "bg-emerald-50 text-emerald-600",
  };

  return (
    <Card
      data-testid={`metric-card-${title.toLowerCase().replace(/\s/g, "-")}`}
      className={`hover:shadow-md transition-shadow duration-200 ${onClick ? "cursor-pointer hover:ring-1 hover:ring-slate-200" : ""}`}
      onClick={onClick}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">
              {title}
              {onClick && <ExternalLink className="inline h-3 w-3 ml-1 text-slate-300" />}
            </p>
            <p className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              {value}
            </p>
            {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl ${colorMap[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        {change !== undefined && change !== null && (
          <div className="mt-3 flex items-center gap-1.5">
            {isNeutral ? (
              <span className="text-xs text-slate-400">No change</span>
            ) : isPositive ? (
              <>
                <ChevronUp className="h-3.5 w-3.5 text-emerald-500" />
                <span className="text-xs font-medium text-emerald-600">+{change}%</span>
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5 text-red-500" />
                <span className="text-xs font-medium text-red-600">{change}%</span>
              </>
            )}
            <span className="text-xs text-slate-400">vs prev. period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MeetingTargetTracker({ target, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [newTarget, setNewTarget] = useState(target?.target || 20);
  const progress = target?.progress_pct || 0;
  const actual = target?.actual || 0;
  const targetVal = target?.target || 20;

  const handleSave = async () => {
    const val = parseInt(newTarget, 10);
    if (isNaN(val) || val < 1 || val > 200) {
      toast.error("Target must be between 1 and 200");
      return;
    }
    await onUpdate(val);
    setEditing(false);
  };

  const barWidth = Math.min(progress, 100);
  const isOnTrack = progress >= ((new Date().getDate() / 30) * 100);

  return (
    <Card data-testid="meeting-target-tracker" className="hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Target className="h-4 w-4 text-blue-600" />
            Meeting Target — {target?.month || ""}
          </CardTitle>
          {!editing ? (
            <Button
              data-testid="edit-target-btn"
              variant="ghost"
              size="sm"
              onClick={() => { setNewTarget(targetVal); setEditing(true); }}
              className="h-7 px-2"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <div className="flex items-center gap-1">
              <Input
                data-testid="target-input"
                type="number"
                value={newTarget}
                onChange={(e) => setNewTarget(e.target.value)}
                className="h-7 w-16 text-sm"
                min={1}
                max={200}
              />
              <Button data-testid="save-target-btn" variant="ghost" size="sm" onClick={handleSave} className="h-7 px-1.5 text-emerald-600 hover:text-emerald-700">
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} className="h-7 px-1.5 text-slate-400 hover:text-slate-600">
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <div>
              <span className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                {actual}
              </span>
              <span className="text-lg text-slate-400 ml-1">/ {targetVal}</span>
            </div>
            <span className={`text-sm font-semibold ${isOnTrack ? "text-emerald-600" : "text-amber-600"}`}>
              {progress.toFixed(0)}%
            </span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
            <div
              data-testid="target-progress-bar"
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${barWidth}%`,
                background: isOnTrack
                  ? "linear-gradient(90deg, #10b981, #059669)"
                  : "linear-gradient(90deg, #f59e0b, #d97706)",
              }}
            />
          </div>
          <p className="text-xs text-slate-400">
            {targetVal - actual > 0
              ? `${targetVal - actual} more meetings needed to hit target`
              : "Target reached!"}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function ConversionFunnel({ data }) {
  const navigate = useNavigate();
  if (!data || data.length === 0) return null;

  // Filter to only stages with counts > 0 and group into funnel categories
  const funnelOrder = [
    "Unqualified Leads", "First Contact Made", "Discovery Conversation Held",
    "RFP Received", "Diagnostic / Briefing Phase", "Non-Financial Proposal Sent",
    "Proposal Submitted", "Commercial Negotiation", "Verbal Agreement",
    "Contract Signed", "Onboarding", "Delivery in Progress", "Delivery Completed",
    "Closed – Won", "Closed – Not Proceeding"
  ];

  // Only show stages with data, in order
  const activeStages = funnelOrder
    .map(stage => data.find(d => d.stage === stage))
    .filter(d => d && d.count > 0);

  if (activeStages.length === 0) return null;
  const maxCount = Math.max(...activeStages.map(d => d.count), 1);

  return (
    <Card data-testid="conversion-funnel" className="hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold" style={{ fontFamily: "Manrope, sans-serif" }}>
          Pipeline Conversion Funnel
        </CardTitle>
        <p className="text-xs text-slate-400">{activeStages.reduce((s, d) => s + d.count, 0)} total deals across {activeStages.length} active stages</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {activeStages.map((item, i) => {
            // Funnel width: first = 100%, narrows as it goes
            const widthPct = Math.max(15, (item.count / maxCount) * 100);
            const isWon = item.stage === "Closed – Won" || item.stage === "Delivery Completed";
            const isLost = item.stage === "Closed – Not Proceeding";
            const bgColor = isWon ? "bg-emerald-500" : isLost ? "bg-red-400" : "bg-blue-500";
            const bgLight = isWon ? "bg-emerald-50" : isLost ? "bg-red-50" : "bg-slate-50";

            return (
              <div
                key={item.stage}
                className={`flex items-center gap-3 py-1.5 px-2 rounded ${bgLight} cursor-pointer hover:ring-1 hover:ring-slate-200 transition-all`}
                onClick={() => navigate(`/clients?stage=${encodeURIComponent(item.stage)}`)}
                data-testid={`funnel-stage-${i}`}
              >
                <div className="w-36 text-xs text-slate-600 truncate font-medium shrink-0">
                  {item.stage}
                </div>
                <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${bgColor} rounded-full flex items-center justify-end pr-2 transition-all duration-700 ease-out`}
                    style={{ width: `${widthPct}%`, minWidth: "2rem" }}
                  >
                    <span className="text-xs font-bold text-white">{item.count}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function MonthlyTrend({ data }) {
  if (!data || data.length === 0) return null;

  return (
    <Card data-testid="monthly-trend" className="hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold" style={{ fontFamily: "Manrope, sans-serif" }}>
          6-Month Trend
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data} margin={{ left: 0, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              type="monotone"
              dataKey="meetings"
              stroke={TREND_COLORS.meetings}
              strokeWidth={2}
              dot={{ r: 4 }}
              name="Meetings"
            />
            <Line
              type="monotone"
              dataKey="proposals"
              stroke={TREND_COLORS.proposals}
              strokeWidth={2}
              dot={{ r: 4 }}
              name="Proposals"
            />
            <Line
              type="monotone"
              dataKey="wins"
              stroke={TREND_COLORS.wins}
              strokeWidth={2}
              dot={{ r: 4 }}
              name="Wins"
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function WinRateDonut({ winRate, period }) {
  const navigate = useNavigate();
  const won = winRate?.won || 0;
  const lost = winRate?.lost || 0;
  const rate = winRate?.rate || 0;
  const total = winRate?.total_closed || 0;
  const [drilldown, setDrilldown] = useState(null);
  const [ddLoading, setDdLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editStage, setEditStage] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const loadDrilldown = async (metric) => {
    setDdLoading(true);
    try {
      const res = await fetch(`${API}/commercial/drilldown/${metric}?period=${period || 90}`, { credentials: "include" });
      if (res.ok) setDrilldown(await res.json());
    } catch { /* ignore */ }
    finally { setDdLoading(false); }
  };

  const startEditItem = (e, item) => {
    e.stopPropagation();
    setEditingItem(item.client_id);
    setEditStage(item.stage || "");
  };

  const saveItemEdit = async (e, clientId) => {
    e.stopPropagation();
    setSavingEdit(true);
    try {
      const res = await fetch(`${API}/clients/${clientId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ stage: editStage }),
      });
      if (res.ok) {
        toast.success("Stage updated");
        setEditingItem(null);
        // Refresh drilldown
        if (drilldown?.metric) loadDrilldown(drilldown.metric);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Card data-testid="win-rate-card" className="hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
          <Trophy className="h-4 w-4 text-emerald-600" />
          Win Rate
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-center">
          <div className="relative w-36 h-36">
            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#f1f5f9"
                strokeWidth="3"
              />
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#10b981"
                strokeWidth="3"
                strokeDasharray={`${rate}, 100`}
                strokeLinecap="round"
                className="transition-all duration-1000 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                {rate}%
              </span>
              <span className="text-xs text-slate-400">of {total} decided</span>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-center gap-6 text-sm">
          <button
            data-testid="drilldown-won"
            onClick={() => loadDrilldown("won")}
            className="flex items-center gap-1.5 hover:underline cursor-pointer"
          >
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <span className="text-slate-600">Won: {won}</span>
            <ExternalLink className="h-3 w-3 text-slate-400" />
          </button>
          <button
            data-testid="drilldown-lost"
            onClick={() => loadDrilldown("lost")}
            className="flex items-center gap-1.5 hover:underline cursor-pointer"
          >
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <span className="text-slate-600">Lost: {lost}</span>
            <ExternalLink className="h-3 w-3 text-slate-400" />
          </button>
        </div>

        {/* Drill-down panel */}
        {(drilldown || ddLoading) && (
          <div className="mt-4 border-t pt-3">
            {ddLoading ? (
              <p className="text-xs text-slate-400 text-center">Loading...</p>
            ) : drilldown && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-600 uppercase">
                    {drilldown.metric === "won" ? `Won Deals (${drilldown.count})` : `Lost Deals (${drilldown.count})`}
                  </p>
                  <button onClick={() => setDrilldown(null)} className="text-slate-400 hover:text-slate-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                {drilldown.explanation && (
                  <p className="text-xs text-slate-400 mb-2 flex items-start gap-1">
                    <Info className="h-3 w-3 mt-0.5 shrink-0" />
                    {drilldown.explanation}
                  </p>
                )}
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {drilldown.items?.map((item, i) => (
                    <div
                      key={item.client_id || i}
                      className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50 cursor-pointer text-xs"
                      data-testid={`drilldown-item-${i}`}
                      onClick={() => editingItem !== item.client_id && navigate(`/clients/${item.client_id}`)}
                    >
                      {editingItem === item.client_id ? (
                        <div className="flex items-center gap-2 w-full" onClick={(e) => e.stopPropagation()}>
                          <p className="font-medium text-slate-800 truncate min-w-0 flex-1">{item.company_name}</p>
                          <select
                            value={editStage}
                            onChange={(e) => setEditStage(e.target.value)}
                            className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white"
                            data-testid={`edit-drilldown-stage-${i}`}
                          >
                            <option value="Closed – Won">Closed – Won</option>
                            <option value="Closed – Not Proceeding">Closed – Not Proceeding</option>
                            <option value="Commercial Negotiation">Commercial Negotiation</option>
                            <option value="Verbal Agreement">Verbal Agreement</option>
                            <option value="Proposal Submitted">Proposal Submitted</option>
                            <option value="Delivery in Progress">Delivery in Progress</option>
                          </select>
                          <button onClick={(e) => saveItemEdit(e, item.client_id)} disabled={savingEdit}
                            className="p-0.5 rounded hover:bg-emerald-100 text-emerald-600" data-testid={`save-drilldown-${i}`}>
                            {savingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setEditingItem(null); }}
                            className="p-0.5 rounded hover:bg-red-100 text-red-500" data-testid={`cancel-drilldown-${i}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800 truncate">{item.company_name}</p>
                            <p className="text-slate-400">{item.stage}{item.win_category ? ` (${item.win_category})` : ""}</p>
                          </div>
                          <div className="flex items-center gap-1.5 ml-2">
                            {item.opportunity_value > 0 && (
                              <span className="text-emerald-600 font-medium whitespace-nowrap">
                                {item.opportunity_currency === "USD" ? "$" : "\u20A6"}{(item.opportunity_value || 0).toLocaleString()}
                              </span>
                            )}
                            <button
                              onClick={(e) => startEditItem(e, item)}
                              className="p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600"
                              data-testid={`edit-drilldown-${i}`}
                            >
                              <Pencil className="h-2.5 w-2.5" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {drilldown.items?.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-2">No items found for this period</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}



const DEAL_STAGES = [
  "First Contact Made", "Discovery Conversation Held", "RFP Received",
  "Diagnostic / Briefing Phase", "Proposal Submitted", "Commercial Negotiation",
  "Verbal Agreement", "Contract Signed", "Delivery in Progress",
];

function TopPipelineDeals({ deals, onUpdate }) {
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = (e, deal) => {
    e.stopPropagation();
    setEditingId(deal.opportunity_id || deal.client_id);
    setEditData({
      stage: deal.stage || "",
      opportunity_value: deal.opportunity_value || 0,
      expected_close_date: deal.expected_close_date || "",
    });
  };

  const cancelEdit = (e) => {
    e.stopPropagation();
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async (e, deal) => {
    e.stopPropagation();
    setSaving(true);
    try {
      const body = {};
      if (editData.stage) body.stage = editData.stage;
      if (editData.opportunity_value !== undefined) body.opportunity_value = Number(editData.opportunity_value);
      if (editData.expected_close_date) body.expected_close_date = editData.expected_close_date;

      // Top Pipeline Deals is now sourced from the canonical opportunities
      // collection (Feb 2026) — edit the opportunity row directly so the
      // Revenue page, Pipeline drill drawer and KPI tiles all stay in sync.
      const url = deal.opportunity_id
        ? `${API}/opportunities/${deal.opportunity_id}`
        : `${API}/clients/${deal.client_id}`;
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success("Deal updated");
        setEditingId(null);
        if (onUpdate) onUpdate();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update");
      }
    } catch {
      toast.error("Failed to update deal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="top-deals-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
          <DollarSign className="h-4 w-4 text-emerald-600" />
          Top Pipeline Deals
          <button
            onClick={() => navigate("/revenue")}
            className="ml-auto text-xs font-medium text-blue-600 hover:underline"
            data-testid="open-pipeline-link"
          >
            Open pipeline →
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-slate-100">
          {deals.map((deal, i) => {
            const rowKey = deal.opportunity_id || deal.client_id || i;
            const isEditing = editingId === (deal.opportunity_id || deal.client_id);
            return (
            <div
              key={rowKey}
              data-testid={`top-deal-${i}`}
              className={`py-2.5 transition-colors ${isEditing ? "bg-slate-50 rounded-lg px-2 -mx-2" : "hover:bg-slate-50/50 cursor-pointer"}`}
              onClick={() => {
                if (isEditing) return;
                // Navigate to the opportunity in the Revenue / Pipeline view
                // so the user lands on the canonical pipeline drill-down.
                if (deal.opportunity_id) {
                  navigate(`/revenue?opp=${deal.opportunity_id}`);
                } else if (deal.client_id) {
                  navigate(`/clients/${deal.client_id}`);
                }
              }}
            >
              {isEditing ? (
                /* ── Inline Edit Mode ── */
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-900">{deal.company_name}</p>
                    <div className="flex gap-1">
                      <button onClick={(e) => saveEdit(e, deal)} disabled={saving} data-testid={`save-deal-${i}`}
                        className="p-1 rounded hover:bg-emerald-100 text-emerald-600 transition-colors">
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      </button>
                      <button onClick={cancelEdit} data-testid={`cancel-deal-${i}`}
                        className="p-1 rounded hover:bg-red-100 text-red-500 transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <select
                      value={editData.stage}
                      onChange={(e) => setEditData({ ...editData, stage: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:ring-1 focus:ring-slate-300 outline-none"
                      data-testid={`edit-stage-${i}`}
                    >
                      {DEAL_STAGES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      value={editData.opportunity_value}
                      onChange={(e) => setEditData({ ...editData, opportunity_value: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:ring-1 focus:ring-slate-300 outline-none"
                      placeholder="Value"
                      data-testid={`edit-value-${i}`}
                    />
                    <input
                      type="date"
                      value={editData.expected_close_date}
                      onChange={(e) => setEditData({ ...editData, expected_close_date: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:ring-1 focus:ring-slate-300 outline-none"
                      data-testid={`edit-date-${i}`}
                    />
                  </div>
                </div>
              ) : (
                /* ── Display Mode ── */
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-bold text-slate-400 w-5">{i + 1}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{deal.company_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-xs">{deal.stage}</Badge>
                        {deal.expected_close_date && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />{deal.expected_close_date}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <span className="text-sm font-bold text-emerald-700 whitespace-nowrap">
                      {deal.opportunity_currency === "USD" ? "$" : "\u20A6"}{(deal.opportunity_value || 0).toLocaleString()}
                    </span>
                    <button
                      onClick={(e) => startEdit(e, deal)}
                      className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors opacity-0 group-hover:opacity-100"
                      style={{ opacity: 1 }}
                      data-testid={`edit-deal-${i}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}






export default function CommercialDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "overview";
  const [dashboard, setDashboard] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [nextBestAction, setNextBestAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [drilldownMetric, setDrilldownMetric] = useState(null);
  const [drilldownData, setDrilldownData] = useState(null);
  const [ddLoading, setDdLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, funnelRes, nbaRes] = await Promise.all([
        fetch(`${API}/commercial/dashboard?period=${period}`, { credentials: "include" }),
        fetch(`${API}/commercial/funnel`, { credentials: "include" }),
        fetch(`${API}/actions/next-best`, { credentials: "include" }),
      ]);

      if (dashRes.ok) setDashboard(await dashRes.json());
      if (funnelRes.ok) setFunnel(await funnelRes.json());
      if (nbaRes.ok) setNextBestAction(await nbaRes.json());
    } catch (err) {
      toast.error("Failed to load commercial data");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDrilldown = async (metric) => {
    if (drilldownMetric === metric) { setDrilldownMetric(null); setDrilldownData(null); return; }
    setDrilldownMetric(metric);
    setDdLoading(true);
    try {
      const res = await fetch(`${API}/commercial/drilldown/${metric}?period=${period}`, { credentials: "include" });
      if (res.ok) setDrilldownData(await res.json());
    } catch { /* ignore */ }
    finally { setDdLoading(false); }
  };

  // Open the shared drill-down panel from data ALREADY in the dashboard payload
  // (no fetch) — used for the "activity not recorded" and "undated wins" groups,
  // so the count on the surface and the names behind it come from one source.
  const openLocalDrilldown = (metric, payload) => {
    if (drilldownMetric === metric) { setDrilldownMetric(null); setDrilldownData(null); return; }
    setDdLoading(false);
    setDrilldownMetric(metric);
    setDrilldownData(payload);
  };

  const handleTargetUpdate = async (newTarget) => {
    try {
      const res = await fetch(`${API}/commercial/targets`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ target_value: newTarget }),
      });
      if (res.ok) {
        const updated = await res.json();
        setDashboard((prev) => ({ ...prev, meeting_target: updated }));
        toast.success("Meeting target updated");
      }
    } catch {
      toast.error("Failed to update target");
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  const d = dashboard;

  return (
    <div data-testid="commercial-dashboard" className="p-6 md:p-8 lg:p-10 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="dashboard-title"
          >
            Dashboard
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Overview of your business development performance
          </p>
        </div>
        <ReconcileButton />
      </div>

      <Tabs value={tabParam} onValueChange={(v) => setSearchParams(v === "overview" ? {} : { tab: v })} className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue">Financials</TabsTrigger>
          <TabsTrigger value="milestones" data-testid="tab-milestones">Milestones</TabsTrigger>
          <TabsTrigger value="cashflow" data-testid="tab-cashflow">Cash Flow</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-8">
          {/* Period Selector — applies ONLY to the activity metrics below, not the money states */}
          <div className="flex items-center gap-2" data-testid="period-selector">
            {[7, 30, 90].map((p) => (
              <Button
                key={p}
                variant={period === p ? "default" : "outline"}
                size="sm"
                onClick={() => setPeriod(p)}
                data-testid={`period-${p}d`}
              >
                {p}d
              </Button>
            ))}
            <span className="text-xs text-slate-400 ml-1">activity window (money states below are point-in-time, not windowed)</span>
          </div>

          {/* Canonical money states — the SINGLE source of truth (/api/finance/summary).
              Point-in-time balances, per currency, collected never converted. */}
          {d?.canonical ? (
            <div data-testid="canonical-money-row">
              <p className="text-xs text-slate-500 mb-2">As at today — not windowed. One source: the four states also shown on the Financials page and the Today panel.</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MoneyStateCard title="Weighted forecast" testid="canonical-pipeline"
                  byCurrency={d.canonical.weighted_pipeline.by_currency}
                  count={d.canonical.weighted_pipeline.deal_count} countLabel="open deals"
                  note="Weighted by stage — a forecast, not revenue. Excludes First Contact, unpriced and archived deals." accent="text-green-700" />
                {d.canonical.open_raw && (
                  <MoneyStateCard title="Total value of open deals" testid="canonical-open-raw"
                    byCurrency={d.canonical.open_raw.by_currency}
                    count={d.canonical.open_raw.deal_count_with_value} countLabel="priced open deals"
                    coverageNote={`${d.canonical.open_raw.deal_count_with_value} of ${d.canonical.open_raw.deal_count_total} open deals have a value — the rest are not counted as zero.`}
                    note="Plain sum of open + proposal deal values, unweighted. Per currency; nothing converted." accent="text-slate-800" />
                )}
                <MoneyStateCard title="Contracted (signed)" testid="canonical-contracted"
                  byCurrency={d.canonical.contracted.by_currency}
                  count={d.canonical.contracted.deal_count} countLabel="signed deals"
                  note="Bankable, not yet cash." accent="text-blue-700" />
                <MoneyStateCard title="Invoiced" testid="canonical-invoiced"
                  byCurrency={d.canonical.invoiced.by_currency}
                  count={d.canonical.invoiced.invoice_count} countLabel="invoices"
                  note="Issued and chaseable." accent="text-amber-700" />
                <MoneyStateCard title="Collected" testid="canonical-collected"
                  byCurrency={d.canonical.collected.by_currency}
                  count={d.canonical.collected.payment_count} countLabel="payments"
                  note="Real money received. Shown in the currency it arrived in; never re-rated." accent="text-teal-700" />
              </div>
            </div>
          ) : (
            <div data-testid="canonical-money-unavailable" className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              The canonical money states could not be computed right now, so they are not shown (rather than showing a misleading zero).
            </div>
          )}

          {/* Activity metrics — legitimately windowed by the selector above */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <MetricCard
              title="Meetings Set"
              value={d?.meetings?.current || 0}
              subtitle={`vs ${d?.meetings?.previous || 0} prev. ${period}d`}
              icon={Users}
              change={d?.meetings?.change_pct}
              color="blue"
              onClick={() => openDrilldown("meetings")}
            />
            <MetricCard
              title="Win Rate"
              value={`${d?.win_rate?.rate || 0}%`}
              subtitle={`${d?.win_rate?.won || 0}W / ${d?.win_rate?.lost || 0}L (last ${period}d)`}
              icon={Trophy}
              color="amber"
              onClick={() => openDrilldown("won")}
            />
            <MetricCard
              title="Proposals Sent"
              value={d?.proposals?.sent || 0}
              subtitle={d?.time_to_proposal?.avg_days > 0 ? `Avg ${d.time_to_proposal.avg_days}d to proposal` : `last ${period}d`}
              icon={FileText}
              change={d?.proposals?.change_pct}
              color="purple"
              onClick={() => openDrilldown("proposals")}
            />
          </div>

          {/* Drill-down Panel */}
          {(drilldownMetric || ddLoading) && (
            <Card data-testid="drilldown-panel" className="border-l-4 border-l-blue-500">
              <CardContent className="pt-4">
                {ddLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  </div>
                ) : drilldownData && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                        {drilldownData.metric === "won" ? `Won Deals (${drilldownData.count})` :
                         drilldownData.metric === "lost" ? `Lost Deals (${drilldownData.count})` :
                         drilldownData.metric === "meetings" ? `Meetings & Calls (${drilldownData.count})` :
                         drilldownData.metric === "proposals" ? `Proposals (${drilldownData.count})` :
                         drilldownData.metric === "signed_revenue" ? `Signed Revenue (${drilldownData.count} deals)` :
                         drilldownData.metric === "pipeline" ? `Active Pipeline (${drilldownData.count} deals)` :
                         drilldownData.metric === "dormant" ? `Dormant Deals (${drilldownData.count})` :
                         drilldownData.metric === "activity_not_recorded" ? `Activity Not Recorded — can't tell (${drilldownData.count})` :
                         drilldownData.metric === "wins_undated" ? `Wins With No Recorded Date (${drilldownData.count})` :
                         drilldownData.metric === "pipeline_coverage" ? `Staged Deals With No Value (${drilldownData.count})` :
                         `${drilldownData.metric} (${drilldownData.count})`}
                      </h3>
                      <button onClick={() => { setDrilldownMetric(null); setDrilldownData(null); }} className="text-slate-400 hover:text-slate-600">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    {drilldownData.explanation && (
                      <p className="text-xs text-slate-400 mb-3 flex items-start gap-1">
                        <Info className="h-3 w-3 mt-0.5 shrink-0" />
                        {drilldownData.explanation}
                      </p>
                    )}
                    <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                      {drilldownData.items?.map((item, i) => (
                        <div
                          key={item.client_id || item.proposal_id || i}
                          onClick={() => item.client_id && navigate(`/clients/${item.client_id}`)}
                          className={`flex items-center justify-between py-2.5 px-2 ${item.client_id ? "cursor-pointer hover:bg-slate-50" : ""}`}
                          data-testid={`drilldown-row-${i}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {item.company_name || item.client_name || item.title || "Unknown"}
                            </p>
                            <p className="text-xs text-slate-400">
                              {item.stage || item.type || item.status || ""}
                              {item.win_category ? ` — ${item.win_category}` : ""}
                              {item.subject ? ` — ${item.subject}` : ""}
                              {item.date ? ` — ${new Date(item.date).toLocaleDateString()}` : ""}
                              {item.stage_date ? ` — ${new Date(item.stage_date).toLocaleDateString()}` : ""}
                              {item.last_activity ? ` — Last activity: ${new Date(item.last_activity).toLocaleDateString()}` : ""}
                              {typeof item.days_in_stage === "number" ? ` — ${item.days_in_stage}d in stage` : (item.days_in_stage === null && item.stage ? " — time in stage unknown" : "")}
                              {item.severity === "critical" ? " — CRITICAL" : item.severity === "warning" ? " — Warning" : ""}
                              {item.account_manager_name ? ` | ${item.account_manager_name}` : ""}
                            </p>
                          </div>
                          {(item.opportunity_value > 0) && (
                            <span className="text-sm text-emerald-600 font-semibold ml-3 whitespace-nowrap">
                              {item.opportunity_currency === "USD" ? "$" : item.opportunity_currency === "GBP" ? "\u00A3" : "\u20A6"}
                              {(item.opportunity_value || 0).toLocaleString()}
                            </span>
                          )}
                          {item.client_id && <ExternalLink className="h-3.5 w-3.5 text-slate-300 ml-2 shrink-0" />}
                        </div>
                      ))}
                      {drilldownData.items?.length === 0 && (
                        <p className="text-sm text-slate-400 text-center py-4">No items found for this period</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Rex Operating Metrics + Rollout moved to Admin Console */}

          {/* Item 27 — pipeline coverage. States what the pipeline value covers and what it
              cannot. Neutral: unpriced deals are not neglected, just without a number. The two
              figures are never mixed into one total. Names (company, stage, owner) = work list. */}
          {d?.pipeline_coverage?.unpriced_count > 0 && (
            <Card className="border border-slate-200 bg-slate-50/60" data-testid="pipeline-coverage-panel">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <Info className="w-5 h-5 text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-slate-700 leading-snug">
                        <span className="text-lg font-bold" data-testid="coverage-priced">{fmtCash(d.pipeline_coverage.priced_value)}</span>
                        <span className="text-slate-500"> across {d.pipeline_coverage.priced_count} priced deals</span>
                        <span className="text-slate-400"> · </span>
                        <span className="text-lg font-bold text-slate-700" data-testid="coverage-unpriced">{d.pipeline_coverage.unpriced_count}</span>
                        <span className="text-slate-500"> deals at RFP Received or beyond carry no value</span>
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        The pipeline value covers the priced deals only — the unpriced ones are not counted as zero and not estimated. They are work: one figure away from being visible.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-slate-600 h-6 text-xs px-2 shrink-0"
                    onClick={() => openLocalDrilldown("pipeline_coverage", {
                      metric: "pipeline_coverage",
                      count: d.pipeline_coverage.unpriced_count,
                      items: d.pipeline_coverage.unpriced_deals || [],
                      explanation: "Live deals at RFP Received or beyond with no value on them. The pipeline figure excludes these; here is the list to price up — company, stage, time in stage and owner.",
                    })}
                    data-testid="view-coverage-btn"
                  >
                    See which <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pipeline Hygiene Warning */}
          {d?.pipeline_hygiene?.total_dormant > 0 && (
            <Card className="border border-amber-200 bg-amber-50/50" data-testid="pipeline-hygiene-warning">
              <CardContent className="py-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <h3 className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
                      Pipeline Hygiene — {d.pipeline_hygiene.total_dormant} dormant deals
                    </h3>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-amber-700 h-6 text-xs px-2"
                    onClick={() => openDrilldown("dormant")}
                    data-testid="view-dormant-btn"
                  >
                    Review All <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="bg-white rounded-lg p-2.5 border border-amber-200 text-center">
                    <p className="text-lg font-bold text-red-600">{d.pipeline_hygiene.critical_count}</p>
                    <p className="text-[9px] uppercase tracking-wider text-slate-500">Critical (180+ days)</p>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-amber-200 text-center">
                    <p className="text-lg font-bold text-amber-600">{d.pipeline_hygiene.warning_count}</p>
                    <p className="text-[9px] uppercase tracking-wider text-slate-500">Warning (90-180 days)</p>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-amber-200 text-center">
                    <p className="text-lg font-bold text-slate-700">{fmtCash(d.pipeline_hygiene.stale_pipeline_value)}</p>
                    <p className="text-[9px] uppercase tracking-wider text-slate-500">Stale Value</p>
                  </div>
                </div>
                <p className="text-[11px] text-amber-700">
                  These deals have been sitting in the same pipeline stage without activity. Consider archiving, re-engaging, or closing them.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Third outcome — deals we genuinely cannot judge. Neutral, NOT a warning:
              no activity signal was ever recorded, so we do not know, rather than
              accuse them of being neglected. Count leads; names sit one click behind. */}
          {d?.pipeline_hygiene?.activity_not_recorded?.count > 0 && (
            <Card className="border border-slate-200 bg-slate-50/60" data-testid="activity-not-recorded-panel">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <HelpCircle className="w-5 h-5 text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-2xl font-bold text-slate-700 leading-none" data-testid="anr-count">
                        {d.pipeline_hygiene.activity_not_recorded.count}
                        <span className="text-sm font-medium text-slate-400 ml-2">deals we can't tell</span>
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        No activity was ever recorded on these — not dormant, not active, simply unknown.
                        Worth {fmtCash(d.pipeline_hygiene.activity_not_recorded.value)}. Shrinks as every stage change now dates itself.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-slate-600 h-6 text-xs px-2 shrink-0"
                    onClick={() => openLocalDrilldown("activity_not_recorded", {
                      metric: "activity_not_recorded",
                      count: d.pipeline_hygiene.activity_not_recorded.count,
                      items: d.pipeline_hygiene.activity_not_recorded.deals || [],
                      explanation: "Live pipeline deals with no dated stage history and no recorded client contact — we cannot say whether they are moving or stalled.",
                    })}
                    data-testid="view-anr-btn"
                  >
                    See which <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Pipeline Deals */}
          {d?.top_deals?.length > 0 && (
            <TopPipelineDeals deals={d.top_deals} onUpdate={fetchData} />
          )}

          {/* Middle Row: Target + Win Rate */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <MeetingTargetTracker target={d?.meeting_target} onUpdate={handleTargetUpdate} />
            </div>
            <WinRateDonut winRate={d?.win_rate} period={period} />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ConversionFunnel data={funnel} />
            <div className="space-y-3">
              <MonthlyTrend data={d?.monthly_trend} />
              {d?.wins_undated?.count > 0 && (
                <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid="wins-undated-line">
                  <div className="min-w-0">
                    <p className="text-lg font-bold text-slate-700 leading-none" data-testid="wins-undated-count">
                      {d.wins_undated.count}
                      <span className="text-xs font-medium text-slate-400 ml-2">wins with no recorded date</span>
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Real wins we never dated — {fmtCash((d.wins_undated.deals || []).reduce((s, x) => s + (x.opportunity_value || 0), 0))} —
                      so they sit beside the trend, not inside a guessed month.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-slate-600 h-6 text-xs px-2 shrink-0"
                    onClick={() => openLocalDrilldown("wins_undated", {
                      metric: "wins_undated",
                      count: d.wins_undated.count,
                      items: d.wins_undated.deals || [],
                      explanation: "Won/signed deals with no dated stage transition and no close date — real, but not placeable on the timeline until dated.",
                    })}
                    data-testid="view-wins-undated-btn"
                  >
                    See which <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Next Best Action — bottom of dashboard, human language */}
          {nextBestAction?.has_recommendation && nextBestAction?.next_best && (
            <Card data-testid="next-best-action-widget" className="border border-slate-200 bg-slate-50/50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <Zap className="w-4 h-4 text-amber-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-slate-700">
                        <span className="font-medium">Rex suggests: </span>
                        {nextBestAction.next_best.why_now || nextBestAction.next_best.recommended_action}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-slate-600 h-7 text-xs shrink-0"
                    onClick={() => {
                      if (nextBestAction.next_best.brief_id) {
                        navigate(`/commercial-briefs/${nextBestAction.next_best.brief_id}`);
                      } else {
                        navigate("/actions");
                      }
                    }}
                  >
                    View details <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Event Radar Widget */}
          <EventRadarWidget navigate={navigate} />

          {/* Platform Health Widget — moved to Admin Console → System Health (May 2026). */}

          {/* Tender Radar Widget */}
          <TenderRadarWidget navigate={navigate} />
        </TabsContent>

        <TabsContent value="revenue">
          <RevenuePage embedded />
        </TabsContent>

        <TabsContent value="milestones">
          <MilestoneTracker />
        </TabsContent>

        <TabsContent value="cashflow">
          <CashFlowCalendar />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ==================== RECONCILE ALL CLIENTS BUTTON ====================

function ReconcileButton() {
  const [status, setStatus] = useState("idle"); // idle | running | done | error
  const [progress, setProgress] = useState({ progress: 0, total: 0, current_client: "" });
  const [results, setResults] = useState(null);

  const startReconciliation = async () => {
    if (status === "running") return;
    if (!window.confirm("This will analyze notes for all ACTIVE clients (contacted in last 90 days) with AI and update their records. Dormant clients are skipped. Continue?")) return;

    setStatus("running");
    setProgress({ progress: 0, total: 0, current_client: "Starting..." });
    setResults(null);

    try {
      const res = await fetch(`${API}/admin/reconcile-all-clients`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to start");
      }

      // Poll for progress
      const poll = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API}/admin/reconcile-status`, { credentials: "include" });
          if (statusRes.ok) {
            const data = await statusRes.json();
            setProgress({ progress: data.progress, total: data.total, current_client: data.current_client });

            if (!data.running && data.results) {
              clearInterval(poll);
              setResults(data.results);
              setStatus(data.results.error ? "error" : "done");
            }
          }
        } catch {}
      }, 2000);
    } catch (e) {
      setStatus("error");
      setResults({ error: e.message });
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid="reconcile-section">
      <Button
        onClick={startReconciliation}
        disabled={status === "running"}
        variant={status === "done" ? "outline" : "default"}
        size="sm"
        className={status === "running" ? "animate-pulse" : ""}
        data-testid="reconcile-all-btn"
      >
        {status === "running" ? (
          <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Reconciling...</>
        ) : status === "done" ? (
          <><Check className="w-4 h-4 mr-1.5 text-teal-600" /> Reconciled</>
        ) : (
          <><RefreshCw className="w-4 h-4 mr-1.5" /> Reconcile All Clients</>
        )}
      </Button>

      {status === "running" && progress.total > 0 && (
        <div className="text-xs text-slate-500" data-testid="reconcile-progress">
          <span className="font-medium">{progress.progress}/{progress.total}</span>
          {progress.current_client && <span className="ml-1.5 text-slate-400">({progress.current_client})</span>}
        </div>
      )}

      {status === "done" && results && !results.error && (
        <div className="text-xs text-slate-500" data-testid="reconcile-results">
          <span className="text-teal-600 font-medium">{results.updated} updated</span>
          {results.skipped > 0 && <span className="ml-1.5">{results.skipped} skipped (no notes)</span>}
          {results.failed > 0 && <span className="ml-1.5 text-red-500">{results.failed} failed</span>}
          {results.total_clients_in_db > 0 && (
            <span className="ml-1.5 text-slate-400">({results.total_active} active of {results.total_clients_in_db})</span>
          )}
        </div>
      )}

      {status === "error" && (
        <span className="text-xs text-red-500" data-testid="reconcile-error">
          Error: {results?.error || "Unknown"}
        </span>
      )}
    </div>
  );
}


// ==================== MILESTONE TRACKER ====================

function MilestoneTracker() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overdue");

  useEffect(() => {
    fetchMilestones();
  }, []);

  const fetchMilestones = async () => {
    try {
      const res = await fetch(`${API}/opportunities/metrics/milestone-tracker`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch {
      toast.error("Failed to load milestone data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <DollarSign className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No milestone data available.</p>
          <p className="text-xs text-slate-400 mt-1">Add payment milestones to your opportunities to see tracking here.</p>
        </CardContent>
      </Card>
    );
  }

  const st = data.states || {};
  const cr = data.collection_rate || { by_currency: {} };
  const dq = data.data_quality || {};
  const dqCount = (dq.date_anomalies?.length || 0) + (dq.past_forecast?.length || 0) + (dq.missing_currency_count || 0) + (dq.missing_date_count || 0);
  const rateExceeds = Object.values(cr.by_currency || {}).some((v) => v > 100);
  const sections = {
    overdue: { label: "Overdue", items: data.overdue?.entries || [], byCur: data.overdue?.by_currency, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
    "30": { label: "Next 30 Days", items: data.windows?.["30"]?.entries || [], byCur: data.windows?.["30"]?.by_currency, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
    "60": { label: "30-60 Days", items: data.windows?.["60"]?.entries || [], byCur: data.windows?.["60"]?.by_currency, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
    "90": { label: "60-90 Days", items: data.windows?.["90"]?.entries || [], byCur: data.windows?.["90"]?.by_currency, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200" },
  };
  const active = sections[activeTab] || sections.overdue;

  return (
    <div className="space-y-6" data-testid="milestone-tracker-dashboard">
      <p className="text-xs text-slate-400">One source: services.money — the same entries as the four states and Cash Flow. Per currency; nothing flattened to a single number.</p>

      {/* Data-quality notice — surfaced, not hidden */}
      {dqCount > 0 && (
        <div data-testid="milestone-data-quality" className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-xs text-orange-800 space-y-1">
          <p className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Data-quality items to review ({dqCount})</p>
          {dq.date_anomalies?.length > 0 && <p>{dq.date_anomalies.length} entr{dq.date_anomalies.length === 1 ? "y has" : "ies have"} an implausibly old scheduled date (e.g. {dq.date_anomalies[0]?.scheduled_date}) — likely a data-entry or default-date error; shown, not hidden.</p>}
          {dq.past_forecast?.length > 0 && <p>{dq.past_forecast.length} forecast entr{dq.past_forecast.length === 1 ? "y has" : "ies have"} a date in the past (a slipped forecast — not counted as overdue, which is invoiced-only).</p>}
          {dq.missing_currency_count > 0 && <p>{dq.missing_currency_count} entr{dq.missing_currency_count === 1 ? "y has" : "ies have"} no currency (excluded from any converted total, never defaulted to NGN).</p>}
          {dq.missing_date_count > 0 && <p>{dq.missing_date_count} forecast/invoiced entr{dq.missing_date_count === 1 ? "y has" : "ies have"} no scheduled date (cannot be phased into a month).</p>}
        </div>
      )}

      {/* Summary Row — per currency, with record counts */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <MoneyStateCard title="Forecast" testid="milestone-forecast-card" accent="text-indigo-600"
          byCurrency={st.forecast?.by_currency} count={st.forecast?.count} countLabel="entries" />
        <MoneyStateCard title="Invoiced" testid="milestone-invoiced-card" accent="text-amber-600"
          byCurrency={st.invoiced?.by_currency} count={st.invoiced?.count} countLabel="entries" />
        <MoneyStateCard title="Collected" testid="milestone-collected-card" accent="text-teal-600"
          byCurrency={st.collected?.by_currency} count={st.collected?.count} countLabel="payments"
          note="Never re-rated to one currency."
          extra={Object.keys(cr.by_currency || {}).length > 0 && (
            <div className="text-[10px] text-teal-600 mt-1" data-testid="milestone-collection-rate">
              <span>Collection rate: {Object.entries(cr.by_currency).map(([c, v]) => `${CCY_SYMBOL[c] || c} ${v}%`).join(" · ")}</span>
              <span className="block text-slate-400 mt-0.5">collected ÷ (collected + invoiced outstanding), per currency</span>
              {rateExceeds && <span className="block text-orange-600 mt-0.5">A rate above 100% means payments exist with no matching outstanding invoice — check the ledger.</span>}
            </div>
          )} />
        <MoneyStateCard title="Overdue" testid="milestone-overdue-summary" accent="text-red-600"
          byCurrency={data.overdue?.by_currency} count={data.overdue?.count} countLabel="invoices"
          note="Invoiced and past due; not yet collected." />
        <MoneyStateCard title="Received (past cash)" testid="milestone-received-card" accent="text-teal-700"
          byCurrency={data.received?.by_currency} count={data.received?.count} countLabel="payments"
          note="Money already in. Shown separately from a forward view." />
      </div>

      {/* Milestone Horizon Tabs (overdue + forward windows) */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2 flex-wrap">
            {Object.entries(sections).map(([key, sec]) => (
              <Button
                key={key}
                size="sm"
                variant={activeTab === key ? "default" : "outline"}
                className={`text-xs ${activeTab === key && key === "overdue" ? "bg-red-600 hover:bg-red-700" : ""}`}
                onClick={() => setActiveTab(key)}
                data-testid={`milestone-tab-${key}`}
              >
                {sec.label} ({sec.items.length})
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {active.items.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">
              {activeTab === "overdue" ? "No overdue milestones" : `No milestones in this window`}
            </p>
          ) : (
            <div className="space-y-2" data-testid={`milestone-list-${activeTab}`}>
              <div className="flex items-center justify-between text-xs text-slate-400 pb-1 border-b border-slate-100 px-2">
                <span>{active.items.length} milestones</span>
                <span className={`font-semibold ${active.color}`}>{fmtCcy(active.byCur)}</span>
              </div>
              {active.items.map((entry) => (
                <div
                  key={entry.entry_id || `${entry.company_name}-${entry.scheduled_date}`}
                  className={`flex items-center justify-between p-3 rounded-lg ${active.bg} border ${active.border} hover:shadow-sm transition-shadow ${entry.client_id ? "cursor-pointer" : ""}`}
                  onClick={() => entry.client_id && navigate(`/clients/${entry.client_id}`)}
                  data-testid={`milestone-entry-${entry.entry_id || "ledger"}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{entry.company_name}</p>
                    <p className="text-xs text-slate-500 truncate">{entry.opportunity_name} — {entry.description || entry.category}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-slate-400">{entry.scheduled_date}</span>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[entry.status] || ""}`}>{entry.status}</Badge>
                      {entry.days_overdue > 0 && (
                        <span className="text-[10px] text-red-500 font-medium">{entry.days_overdue}d overdue</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right ml-4 shrink-0">
                    <p className={`text-sm font-bold ${active.color}`}>{fmtCcy({ [entry.currency || "__NO_CURRENCY__"]: entry.amount })}</p>
                    <Badge variant="outline" className="text-[10px] mt-0.5">{entry.category}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ==================== CASH FLOW CALENDAR ====================

function MoneyStateCard({ title, byCurrency, count, countLabel, note, accent = "text-slate-900", testid, extra, coverageNote }) {
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={testid}>
      <CardContent className="pt-4 pb-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{title}</p>
        <p className={`text-xl font-bold mt-1 leading-tight ${accent}`} style={{ fontFamily: "Manrope, sans-serif" }}>
          {fmtCcy(byCurrency)}
        </p>
        {(count !== undefined && count !== null) && (
          <p className="text-[10px] text-slate-500 mt-0.5">{count} {pluralLabel(count, countLabel || "records")}</p>
        )}
        {coverageNote && (
          <p className="text-[10px] text-slate-500 mt-1 flex items-start gap-1 border-t border-slate-100 pt-1" data-testid="tile-coverage-note">
            <span className="text-slate-300 leading-none">◦</span>
            <span>{coverageNote}</span>
          </p>
        )}
        {note && <p className="text-[10px] text-slate-400 mt-0.5">{note}</p>}
        {extra}
      </CardContent>
    </Card>
  );
}

function CashFlowCalendar() {
  const [cf, setCf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMonth, setExpandedMonth] = useState(null);

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    try {
      const res = await fetch(`${API}/opportunities/metrics/cashflow`, { credentials: "include" });
      if (res.ok) setCf(await res.json());
    } catch {
      toast.error("Failed to load cash flow data");
    } finally { setLoading(false); }
  };

  if (loading) return (<div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>);
  if (!cf) return (<Card><CardContent className="py-12 text-center"><Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No cash flow data available.</p></CardContent></Card>);

  const st = cf.states || {};
  const overdue = cf.overdue || { by_currency: {}, entries: [], count: 0 };
  const received = cf.received || { by_currency: {}, entries: [], count: 0 };
  const forwardMonths = cf.forward_months || [];
  const dq = cf.data_quality || {};
  const ci = dq.client_integrity && !dq.client_integrity.error ? dq.client_integrity : null;
  const dqCount = (dq.date_anomalies?.length || 0) + (dq.past_forecast?.length || 0) + (dq.missing_currency_count || 0) + (dq.missing_date_count || 0) + (ci ? ci.duplicate_group_count : 0);
  const chartData = forwardMonths.map((m) => ({ month: m.month, label: m.label, expected: m.converted?.total || 0 }));
  const forwardTotal = cf.forward_total || { by_currency: {}, converted: {} };
  const fx = cf.fx_meta || {};

  return (
    <div className="space-y-6" data-testid="cashflow-calendar">
      <p className="text-xs text-slate-400">One source: services.money. The forward view is future forecast + invoiced only; overdue and received (past cash) are separate buckets so nothing is double-counted and the chart reconciles to the cards.</p>

      {dqCount > 0 && (
        <div data-testid="cashflow-data-quality" className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-xs text-orange-800 space-y-1">
          <p className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Data-quality items ({dqCount})</p>
          {dq.date_anomalies?.length > 0 && <p>{dq.date_anomalies.length} entr{dq.date_anomalies.length === 1 ? "y has" : "ies have"} an implausibly old date (e.g. {dq.date_anomalies[0]?.scheduled_date}). Shown for correction, not hidden or placed on a forward month.</p>}
          {dq.past_forecast?.length > 0 && <p>{dq.past_forecast.length} forecast entr{dq.past_forecast.length === 1 ? "y is" : "ies are"} past its date (slipped; not overdue, which is invoiced-only).</p>}
          {dq.missing_currency_count > 0 && <p>{dq.missing_currency_count} entr{dq.missing_currency_count === 1 ? "y has" : "ies have"} no currency (excluded from conversions, never defaulted to NGN).</p>}
          {dq.missing_date_count > 0 && <p>{dq.missing_date_count} entr{dq.missing_date_count === 1 ? "y has" : "ies have"} no date (cannot be phased into a month).</p>}
          {ci && ci.duplicate_group_count > 0 && (
            <p data-testid="dq-client-duplicates">
              {ci.duplicate_group_count} set{ci.duplicate_group_count === 1 ? "" : "s"} of client records share the same normalised name (e.g. "{ci.duplicate_groups?.[0]?.normalised}"){ci.degraded_record_count > 0 ? `, leaving ${ci.degraded_record_count} record${ci.degraded_record_count === 1 ? "" : "s"} reachable only by exact name` : ""}. One client is split across two records — needs an owner-decided merge, not a rename.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MoneyStateCard title="Forecast (all)" testid="cashflow-forecast-card" accent="text-indigo-600" byCurrency={st.forecast?.by_currency} count={st.forecast?.count} countLabel="entries" />
        <MoneyStateCard title="Invoiced (all)" testid="cashflow-invoiced-card" accent="text-amber-600" byCurrency={st.invoiced?.by_currency} count={st.invoiced?.count} countLabel="entries" />
        <MoneyStateCard title="Overdue" testid="cashflow-overdue-card" accent="text-red-600" byCurrency={overdue.by_currency} count={overdue.count} countLabel="invoices" note="Invoiced, past due, not collected." />
        <MoneyStateCard title="Received (past cash)" testid="cashflow-received-card" accent="text-teal-700" byCurrency={received.by_currency} count={received.count} countLabel="payments" note="Collected — excluded from the forward view." />
      </div>

      <Card data-testid="expected-revenue-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <TrendingUp className="h-4 w-4 text-emerald-600" /> Expected Revenue by Month (forward)
            <span className="text-xs font-normal text-slate-500 ml-2">Total: {fmtCcy(forwardTotal.by_currency)}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10" data-testid="cashflow-forward-empty">No future forecast or invoiced entries. All current money sits in Overdue or Received above — the forward view is honestly empty rather than showing a misleading figure.</p>
          ) : (<>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" fontSize={11} tick={{ fill: "#64748b" }} />
                <YAxis tickFormatter={(v) => { if (v >= 1e9) return `${(v / 1e9).toFixed(0)}B`; if (v >= 1e6) return `${(v / 1e6).toFixed(0)}M`; if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`; return v; }} fontSize={11} tick={{ fill: "#64748b" }} width={55} />
                <Tooltip formatter={(value) => [fmtCash(value), "Expected (converted)"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="expected" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-slate-400 mt-1" data-testid="cashflow-fx-note">
              Bar height is an indicative base-currency (NGN) conversion; the per-currency detail below is exact. Collected is never converted and is not in this chart.
              {fx.as_of
                ? <span className={fx.stale ? "block text-orange-600 mt-0.5" : "block text-slate-400 mt-0.5"}>
                    {fx.stale
                      ? `⚠ Working FX rates are STALE — the oldest was set ${fx.oldest_age_days} days ago (${fx.as_of}), past the ${fx.stale_after_days}-day threshold. Treat converted figures with caution and update the rates.`
                      : `Converted at working FX rates as at ${fx.as_of}${fx.oldest_age_days != null ? ` (oldest ${fx.oldest_age_days}d old)` : ""}.`}
                  </span>
                : <span className="block text-orange-600 mt-0.5">No working FX rate is set, so foreign amounts are not converted — shown per currency only.</span>}
            </p>
          </>)}
        </CardContent>
      </Card>

      {overdue.entries?.length > 0 && (
        <Card data-testid="cashflow-overdue-list">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold flex items-center gap-2 text-red-600"><AlertTriangle className="h-4 w-4" /> Overdue — {fmtCcy(overdue.by_currency)} ({overdue.count})</CardTitle></CardHeader>
          <CardContent><div className="space-y-2">
            {overdue.entries.map((e, i) => (<div key={e.entry_id || i} className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200">
              <div className="min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{e.company_name}</p><p className="text-xs text-slate-500">{e.opportunity_name} — {e.scheduled_date}{e.days_overdue > 0 ? ` · ${e.days_overdue}d overdue` : ""}</p></div>
              <p className="text-sm font-bold text-red-600 ml-3">{fmtCcy({ [e.currency || "__NO_CURRENCY__"]: e.amount })}</p>
            </div>))}
          </div></CardContent>
        </Card>
      )}

      {forwardMonths.length > 0 && (
        <div className="space-y-3" data-testid="cashflow-forward-months">
          {forwardMonths.map((m) => {
            const isExpanded = expandedMonth === m.month;
            return (<Card key={m.month} className="overflow-hidden" data-testid={`cashflow-month-${m.month}`}>
              <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50" onClick={() => setExpandedMonth(isExpanded ? null : m.month)}>
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400 rotate-90" />}
                  <div><p className="font-semibold text-slate-900">{m.label}</p><p className="text-xs text-slate-400">{m.count} {m.count === 1 ? "entry" : "entries"}</p></div>
                </div>
                <p className="text-lg font-bold text-slate-900">{fmtCcy(m.by_currency)}</p>
              </div>
              {isExpanded && (<div className="border-t border-slate-100"><table className="w-full text-sm"><thead className="bg-slate-50"><tr>
                <th className="text-left p-3 font-medium text-slate-500 text-xs">Date</th><th className="text-left p-3 font-medium text-slate-500 text-xs">Client / Opportunity</th><th className="text-left p-3 font-medium text-slate-500 text-xs">Status</th><th className="text-right p-3 font-medium text-slate-500 text-xs">Amount</th>
              </tr></thead><tbody>
                {m.entries.map((e, i) => (<tr key={e.entry_id || i} className="border-t border-slate-50"><td className="p-3 text-slate-700 whitespace-nowrap">{e.scheduled_date}</td><td className="p-3"><p className="font-medium text-slate-900 text-xs">{e.company_name}</p><p className="text-slate-400 text-[11px]">{e.opportunity_name}</p></td><td className="p-3"><span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium border ${STATUS_COLORS[e.status] || "bg-slate-100 text-slate-600"}`}>{e.status}</span></td><td className="p-3 text-right font-semibold text-slate-900">{fmtCcy({ [e.currency || "__NO_CURRENCY__"]: e.amount })}</td></tr>))}
              </tbody></table></div>)}
            </Card>);
          })}
        </div>
      )}

      {received.entries?.length > 0 && (
        <Card data-testid="cashflow-received-list">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold flex items-center gap-2 text-teal-700"><Trophy className="h-4 w-4" /> Received — {fmtCcy(received.by_currency)} ({received.count})</CardTitle></CardHeader>
          <CardContent><div className="divide-y divide-slate-100">
            {received.entries.map((e, i) => (<div key={e.entry_id || i} className="flex items-center justify-between py-2.5"><div className="min-w-0"><p className="text-sm font-medium text-slate-800">{e.company_name}</p><p className="text-xs text-slate-400">{e.opportunity_name} — {e.scheduled_date || "payment"}</p></div><span className="text-sm font-bold text-teal-600 ml-3 whitespace-nowrap">{fmtCcy({ [e.currency || "__NO_CURRENCY__"]: e.amount })}</span></div>))}
          </div></CardContent>
        </Card>
      )}
    </div>
  );
}


// ==================== EVENT RADAR WIDGET ====================

function EventRadarWidget({ navigate }) {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch(`${API}/events/upcoming?limit=3`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setEvents(data.events || []);
        }
      } catch { /* silently fail */ }
      setLoaded(true);
    };
    fetchEvents();
  }, []);

  if (!loaded || events.length === 0) return null;

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return d; }
  };

  const daysUntil = (d) => {
    if (!d) return null;
    return Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
  };

  return (
    <Card data-testid="event-radar-widget" className="border border-slate-200">
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Upcoming Events</h3>
          </div>
          <Button size="sm" variant="ghost" className="text-blue-600 h-6 text-xs px-2" onClick={() => navigate("/event-radar")}>
            View All <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        <div className="space-y-2">
          {events.map((event) => {
            const days = daysUntil(event.date_start);
            const score = event.relevance?.score || 0;
            return (
              <div
                key={event.event_id}
                className="flex items-center justify-between rounded-lg p-2.5 bg-slate-50 border border-slate-200 cursor-pointer hover:bg-slate-100 transition-all"
                onClick={() => navigate("/event-radar")}
                data-testid={`widget-event-${event.event_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    score >= 70 ? "bg-emerald-100" : score >= 40 ? "bg-amber-100" : "bg-slate-100"
                  }`}>
                    <span className={`text-[10px] font-bold ${
                      score >= 70 ? "text-emerald-700" : score >= 40 ? "text-amber-700" : "text-slate-600"
                    }`}>{score}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{event.name}</p>
                    <p className="text-[10px] text-slate-400">{formatDate(event.date_start)} — {event.city || event.country}</p>
                  </div>
                </div>
                {days !== null && days >= 0 && (
                  <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded shrink-0 ${
                    days <= 14 ? "bg-red-100 text-red-700" : days <= 30 ? "bg-amber-100 text-amber-700" : "text-slate-500"
                  }`}>
                    {days <= 30 ? `${days}d` : `${Math.ceil(days/7)}w`}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


// ==================== PLATFORM HEALTH WIDGET (moved) ================
// Lifted to /components/PlatformHealthWidget.jsx (May 2026) — it now
// renders under Admin Console → System Health instead of the sales
// dashboard. See CommercialDashboard render above for the empty slot.


// ==================== TENDER RADAR WIDGET ====================

function TenderRadarWidget({ navigate }) {
  const [tenders, setTenders] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchTenders = async () => {
      try {
        const res = await fetch(`${API}/tenders/upcoming?limit=3`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setTenders(data.tenders || []);
        }
      } catch { /* silently fail */ }
      setLoaded(true);
    };
    fetchTenders();
  }, []);

  if (!loaded || tenders.length === 0) return null;

  const formatDate = (d) => {
    if (!d) return "No deadline";
    try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return d; }
  };

  const daysUntil = (d) => {
    if (!d) return null;
    return Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
  };

  return (
    <Card data-testid="tender-radar-widget" className="border border-slate-200">
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Tender Radar</h3>
          </div>
          <Button size="sm" variant="ghost" className="text-blue-600 h-6 text-xs px-2" onClick={() => navigate("/tender-radar")}>
            View All <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        <div className="space-y-2">
          {tenders.map((tender) => {
            const fit = tender.strategic_fit || {};
            const days = daysUntil(tender.deadline);
            const fitColor = fit.fit === "HIGH" ? "bg-emerald-100 text-emerald-700"
              : fit.fit === "MEDIUM" ? "bg-amber-100 text-amber-700"
              : "bg-slate-100 text-slate-500";
            return (
              <div
                key={tender.tender_id}
                className="flex items-center justify-between rounded-lg p-2.5 bg-slate-50 border border-slate-200 cursor-pointer hover:bg-slate-100 transition-all"
                onClick={() => navigate("/tender-radar")}
                data-testid={`widget-tender-${tender.tender_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    fit.fit === "HIGH" ? "bg-emerald-100" : fit.fit === "MEDIUM" ? "bg-amber-100" : "bg-slate-100"
                  }`}>
                    <span className={`text-[10px] font-bold ${
                      fit.fit === "HIGH" ? "text-emerald-700" : fit.fit === "MEDIUM" ? "text-amber-700" : "text-slate-600"
                    }`}>{fit.score || 0}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{tender.title}</p>
                    <p className="text-[10px] text-slate-400">
                      {tender.issuing_organisation || tender.country || "Unknown"}
                      {tender.deadline && ` — ${formatDate(tender.deadline)}`}
                    </p>
                  </div>
                </div>
                {days !== null && days >= 0 && (
                  <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded shrink-0 ${
                    days <= 7 ? "bg-red-100 text-red-700" : days <= 14 ? "bg-amber-100 text-amber-700" : "text-slate-500"
                  }`}>
                    {days}d
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
